import { EventBus } from '~/plugins/event-bus';

export function pwaBadge() {
  /**
   * Check if the Badging API is supported
   */
  const isSupported = () => {
    if (!process.client) return false;
    return 'setAppBadge' in navigator && 'clearAppBadge' in navigator;
  };

  /**
   * Check if running as installed PWA
   */
  const isPWA = () => {
    if (!process.client) return false;

    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }

    if (window.navigator.standalone === true) {
      return true;
    }

    return false;
  };

  /**
   * Set the badge count
   * @param {number} count - The number to display on the badge
   */
  const setBadge = async (count) => {
    if (!isSupported()) return false;

    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
      return true;
    } catch (error) {
      console.warn('Failed to set app badge:', error);
      return false;
    }
  };

  /**
   * Clear the badge
   */
  const clearBadge = async () => {
    if (!isSupported()) return false;

    try {
      await navigator.clearAppBadge();
      return true;
    } catch (error) {
      console.warn('Failed to clear app badge:', error);
      return false;
    }
  };

  /**
   * Get the current total notification count
   */
  const getTotalCount = () => {
    const messages = EventBus.$notifications.messages || 0;
    const unreaded = EventBus.$notifications.unreaded || 0;
    const connections = EventBus.$notifications.connections || 0;

    return messages + unreaded + connections;
  };

  /**
   * Update the badge based on current EventBus notifications
   */
  const updateBadgeFromNotifications = async () => {
    const total = getTotalCount();
    return await setBadge(total);
  };

  /**
   * Initialize OneSignal notification listener for badge updates
   */
  const initOneSignalBadgeListener = () => {
    if (!process.client) return;

    // Use OneSignalDeferred to ensure SDK is ready
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async function(OneSignal) {
      try {
        // Listen for notification received (foreground)
        OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
          // Update badge when notification is received in foreground
          setTimeout(() => {
            updateBadgeFromNotifications();
          }, 500);
        });

        // Listen for notification clicked
        OneSignal.Notifications.addEventListener('click', () => {
          // Small delay to allow EventBus to update
          setTimeout(() => {
            updateBadgeFromNotifications();
          }, 500);
        });

        // Listen for notification dismissed
        OneSignal.Notifications.addEventListener('dismiss', () => {
          setTimeout(() => {
            updateBadgeFromNotifications();
          }, 500);
        });

        // Listen for permission changes
        OneSignal.Notifications.addEventListener('permissionChange', (permission) => {
          if (permission) {
            updateBadgeFromNotifications();
          }
        });
      } catch (error) {
        console.warn('Failed to setup OneSignal badge listeners:', error);
      }
    });
  };

  /**
   * Start watching EventBus notifications and auto-update badge
   * Should be called once when the app initializes
   */
  const startWatching = () => {
    if (!process.client || !isSupported()) return;

    // Initial update
    updateBadgeFromNotifications();

    // Initialize OneSignal badge listener
    initOneSignalBadgeListener();

    // Watch for notification changes via EventBus events
    EventBus.$on('updateNotifications', () => {
      // Small delay to ensure EventBus.$notifications has been updated
      setTimeout(() => {
        updateBadgeFromNotifications();
      }, 100);
    });

    EventBus.$on('NewMessage', () => {
      setTimeout(() => {
        updateBadgeFromNotifications();
      }, 100);
    });

    EventBus.$on('createMatches', () => {
      setTimeout(() => {
        updateBadgeFromNotifications();
      }, 100);
    });

    EventBus.$on('newConnection', () => {
      setTimeout(() => {
        updateBadgeFromNotifications();
      }, 100);
    });

    EventBus.$on('badgeUpdated', () => {
      updateBadgeFromNotifications();
    });

    // Listen for badge clear request
    EventBus.$on('clearBadge', () => {
      clearBadge();
    });
  };

  /**
   * Stop watching for notification changes
   */
  const stopWatching = () => {
    EventBus.$off('updateNotifications');
    EventBus.$off('NewMessage');
    EventBus.$off('createMatches');
    EventBus.$off('newConnection');
    EventBus.$off('badgeUpdated');
    EventBus.$off('clearBadge');
  };

  return {
    isSupported,
    isPWA,
    setBadge,
    clearBadge,
    getTotalCount,
    updateBadgeFromNotifications,
    initOneSignalBadgeListener,
    startWatching,
    stopWatching,
  };
}
